import { Box } from "@mui/material";
import { CoreMenuItem, CoreSelect, OverflowText } from "@verity/components/core";
import i18n from "../../../../../i18n";
import { FlagLevelBadge } from "../../../../../reports/shared/report-components";
import ArrowIcon from "../../../../../images/reports/backIcon.svg?react";
import styles from "./StudentSelector.module.css";
import dropdownStyles from "./SelectorDropdown.module.css";

type Student = {
	id: number;
	name: string;
	severityLevel?: number;
};

function NavButton({ direction, onClick }: { direction: "back" | "next"; onClick: () => void }) {
	const label = i18n.t(
		direction === "back"
			? "result.components.resultsPage.proctoringResultsViewTSX.previous"
			: "result.components.resultsPage.proctoringResultsViewTSX.next",
	);
	return (
		<Box component="button" type="button" className={styles.navButton} onClick={onClick} aria-label={label}>
			<ArrowIcon aria-hidden="true" className={`${styles.navIcon} ${direction === "back" ? styles.navIconBack : ""}`} />
		</Box>
	);
}

function StudentOptionDisplay({ name, flagLevel }: { name: string; flagLevel: number }) {
	return (
		<div className={dropdownStyles.optionDisplay}>
			<OverflowText className={styles.studentValueLabel}>{name}</OverflowText>
			<span className={dropdownStyles.valueBadge}>
				<FlagLevelBadge flagLevel={flagLevel} />
			</span>
		</div>
	);
}

export type StudentSelectorProps = {
	selectedStudent?: number;
	onStudentChange: (studentId?: number) => void;
	studentList: Student[];
	onBack: () => void;
	onNext: () => void;
	className?: string;
	/** Display only: the name and flag render, but there is no control to open (`SPEC-3.md` §10.3). */
	readOnly?: boolean;
	/**
	 * Replaces the "Test Taker" label and the value beneath it with fixed text, for a surface where this
	 * block is a heading rather than a selection — the public demo uses it as the report card's title.
	 *
	 * Read-only only: with a live dropdown the value is whatever the user picked, and there would be
	 * nothing for a fixed string to describe. Both halves are required together, so the block cannot end
	 * up relabelled on one line and not the other.
	 *
	 * A real DOM replacement rather than a CSS one on purpose. Hiding the stock label with `font-size: 0`
	 * and supplying a new one through `::after` leaves the original in the accessibility tree, so screen
	 * readers announce a label sighted users cannot see; and no CSS property can set an accessible name.
	 */
	heading?: { label: string; value: string };
};

export default function StudentSelector({
	selectedStudent,
	onStudentChange,
	studentList,
	onBack,
	onNext,
	className,
	readOnly,
	heading,
}: StudentSelectorProps) {
	const selected = studentList.find((student) => student.id === selectedStudent);
	if (readOnly) {
		// Not a disabled `CoreSelect`: a greyed-out combobox still reads as a control that might become
		// available. With one test-taker in scope there is nothing to switch to, so the value is text.
		return (
			<div className={`${dropdownStyles.labelWrapper} ${styles.studentLabelWrapper}`}>
				<span className={dropdownStyles.label}>
					{heading?.label ?? i18n.t("result.components.resultsPage.proctoringResultsViewTSX.testTakerSelectorLabel")}
				</span>
				<div className={`${dropdownStyles.container} ${className || ""}`}>
					<div className={`${dropdownStyles.staticValue} ${styles.studentStaticValue}`}>
						{heading ? (
							<OverflowText className={styles.studentValueLabel}>{heading.value}</OverflowText>
						) : (
							selected && <StudentOptionDisplay name={selected.name} flagLevel={selected.severityLevel ?? 0} />
						)}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={`${dropdownStyles.labelWrapper} ${styles.studentLabelWrapper}`}>
			<span className={dropdownStyles.label}>
				{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.testTakerSelectorLabel")}
			</span>
			<div className={`${dropdownStyles.container} ${className || ""}`}>
				{studentList.length > 1 && <NavButton direction="back" onClick={onBack} />}
				<CoreSelect
					className={`${dropdownStyles.dropdown} ${styles.studentDropdown}`}
					label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.testTakerSelectorLabel") as string}
					value={selectedStudent}
					onChange={(event) => {
						const studentId = Number(event.target.value);
						onStudentChange(studentId);
					}}
					renderValue={(selectedValue) => {
						const student = studentList.find((s) => s.id === selectedValue);
						if (!student) return null;
						return <StudentOptionDisplay name={student.name} flagLevel={student.severityLevel ?? 0} />;
					}}
				>
					{studentList.map((student) => (
						<CoreMenuItem key={student.id} value={student.id}>
							<StudentOptionDisplay name={student.name} flagLevel={student.severityLevel ?? 0} />
						</CoreMenuItem>
					))}
				</CoreSelect>
				{studentList.length > 1 && <NavButton direction="next" onClick={onNext} />}
			</div>
		</div>
	);
}
